import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Bucket, WindowKey } from '../../services/executionStatsService';
import ExecutionStatsBadge from './ExecutionStatsBadge';
import RunList, { RunListQuery } from './RunList';
import RunRecovery, { ProblemBucket } from './RunRecovery';
import { useWorkflowRunStats } from './useExecutionSummary';
import { WINDOW_MS, windowLabel } from './executionStatsWindows';
import { dsVar } from '../../theme/tokens';

/** A list of runs to open on, instead of the totals. */
export interface InitialRuns {
    bucket: Bucket | null;
    /** Whose runs the count that was chosen describes; see useWorkflowRunStats. */
    attribution: 'campaign' | 'flow';
}

const PROBLEM_BUCKETS: ProblemBucket[] = ['failed', 'stalled'];

/**
 * A workflow's run counts, and the runs behind any of them, in one place.
 *
 * Choosing a count swaps the figures for the list in place, with a way back,
 * rather than opening a second drawer over the first.
 */
const WorkflowRunPanel: React.FC<{
    campaignId?: string | null;
    flowCode?: string | null;
    defaultWindow?: WindowKey;
    /**
     * Opens straight on these runs, for a reader who chose a count before the
     * drawer opened. Read on mount only: key the panel to change it.
     */
    initialRuns?: InitialRuns;
    /** Beside the "Runs" heading, such as a link to the full run health page. */
    headerAction?: React.ReactNode;
}> = ({ campaignId, flowCode, defaultWindow = '7d', initialRuns, headerAction }) => {
    const stats = useWorkflowRunStats({ campaignId, flowCode });
    const [window, setWindow] = useState<WindowKey>(defaultWindow);

    const selectionFor = (attribution: 'campaign' | 'flow') => (
        attribution === 'campaign'
            ? { scope: 'flow' as const, keyType: 'campaign' as const, key: campaignId as string }
            : { scope: 'flow' as const, keyType: 'code' as const, key: flowCode as string, groupVersions: true }
    );

    const queryFor = (bucket: Bucket | null, chosen: WindowKey, attribution: 'campaign' | 'flow'): RunListQuery => {
        const to = Date.now();
        return {
            ...selectionFor(attribution),
            from: new Date(to - WINDOW_MS[chosen]).toISOString(),
            to: new Date(to).toISOString(),
            bucket
        };
    };

    const [selection, setSelection] = useState<RunListQuery | null>(() =>
        initialRuns ? queryFor(initialRuns.bucket, defaultWindow, initialRuns.attribution) : null
    );

    const open = (bucket: Bucket | null, chosen: WindowKey, attribution: 'campaign' | 'flow') => {
        setWindow(chosen);
        setSelection(queryFor(bucket, chosen, attribution));
    };

    const counts = stats.data?.counts[window];
    const problems = PROBLEM_BUCKETS.filter((bucket) => (counts?.[bucket] ?? 0) > 0);

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography
                    component="h3"
                    sx={{ fontSize: '0.6875rem', fontWeight: 700, color: dsVar('fg-2'), textTransform: 'uppercase', letterSpacing: '0.06em', m: 0 }}
                >
                    Runs
                </Typography>
                {headerAction}
            </Box>
            {selection ? (
                <Box>
                    <Box
                        component="button"
                        onClick={() => setSelection(null)}
                        sx={{ background: 'none', border: 'none', p: 0, mb: 1, cursor: 'pointer', fontSize: '0.75rem', color: dsVar('accent-fg') }}
                    >
                        ← Back to totals ({windowLabel(window)})
                    </Box>
                    <RunList
                        query={selection}
                        onBucketChange={(bucket) => setSelection((s) => (s ? { ...s, bucket } : s))}
                    />
                </Box>
            ) : (
                <ExecutionStatsBadge
                    state={stats}
                    attribution={stats.attribution}
                    variant="full"
                    defaultWindow={window}
                    onWindowChange={setWindow}
                    onSelectBucket={stats.attribution
                        ? (bucket, chosen) => open(bucket, chosen, stats.attribution as 'campaign' | 'flow')
                        : undefined}
                    insight={stats.attribution && problems.length > 0 && (
                        <RunRecovery selection={selectionFor(stats.attribution)} window={window} problems={problems} />
                    )}
                />
            )}
        </Box>
    );
};

export default WorkflowRunPanel;
